/*
 * CONFIDENTIAL & PROPRIETARY
 *
 * Copyright © 2025 AionTrack. All rights reserved.
 *
 * This file contains proprietary algorithms and trade secrets.
 * Unauthorized use, disclosure, or reproduction is strictly prohibited.
 *
 * File: OTIOTimelineViewer.tsx
 * Contains: OpenTimelineIO timeline rendering with visual diff comparison and VFX
 * shot status highlighting
 * Classification: TRADE SECRET - DO NOT DISTRIBUTE
 */

/**
 * OTIO Timeline Viewer Component
 *
 * Displays the actual timeline clips from OTIO data in an NLE-style layout.
 * Supports View mode (single timeline) and Compare mode (diff visualization).
 */

import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Eye, GitCompare, Wand2, ArrowUpRight, ArrowDownRight, AlertCircle } from 'lucide-react'

// =============================================
// TYPES
// =============================================

interface OTIOClip {
  name: string
  track_name: string
  timeline_position_frames: number
  source_in_frames: number
  source_out_frames: number
  duration_frames: number
  nle_unique_id?: string
}

interface OTIOTrack {
  name: string
  kind: string // 'Video' or 'Audio'
  clips: OTIOClip[]
}

interface ClipDiffInfo {
  clip_name: string
  nle_unique_id?: string
  change_type: 'added' | 'removed' | 'trimmed' | 'moved' | 'metadata' | 'unchanged'
  // For trimmed clips
  frames_trimmed_head?: number
  frames_trimmed_tail?: number
  // For moved clips
  old_timeline_position?: number
  new_timeline_position?: number
}

interface VfxShotChange {
  shot_name: string
  change_type: 'frame_range_changed'
  old_start: number
  old_end: number
  old_duration: number
  new_start: number
  new_end: number
  new_duration: number
  frame_difference: number
  nle_unique_id?: string
}

export interface OTIOTimelineViewerProps {
  // OTIO timeline JSON string
  otioTimeline: string
  // Optional: For compare mode
  compareOtioTimeline?: string
  diffData?: {
    changes: ClipDiffInfo[]
    vfx_changes?: VfxShotChange[]
    summary: {
      added: number
      removed: number
      trimmed: number
      moved: number
      vfx_frame_changes?: number
      retranscode_required?: number
    }
  }
  // Optional: VFX shot data to color clips by status
  vfxShots?: Array<{
    id: string
    shot_name: string
    status: string
    frame_range_start: number
    frame_range_end: number
  }>
  className?: string
}

// =============================================
// HELPER FUNCTIONS
// =============================================

/**
 * Parse OTIO JSON to extract tracks and clips
 */
function parseOTIOTimeline(otioJson: string): { tracks: OTIOTrack[], frameRate: number, duration: number } {
  try {
    const timeline = typeof otioJson === 'string' ? JSON.parse(otioJson) : otioJson

    // Extract global timeline info
    const tracks: OTIOTrack[] = []
    let maxDuration = 0
    let frameRate = 24 // default

    // Get frame rate from timeline if available
    if (timeline.global_start_time?.rate) {
      frameRate = timeline.global_start_time.rate
    }

    // Parse tracks
    if (timeline.tracks?.children) {
      for (const track of timeline.tracks.children) {
        const trackInfo: OTIOTrack = {
          name: track.name || 'Unnamed Track',
          kind: track.kind || 'Video',
          clips: []
        }

        // Skip audio tracks for now unless specified
        if (track.kind === 'Audio') {
          continue
        }

        let timelinePosition = 0

        // Parse clips in track
        if (track.children) {
          for (const item of track.children) {
            if (item.OTIO_SCHEMA === 'Clip.1' || item.OTIO_SCHEMA?.includes('Clip')) {
              const sourceRange = item.source_range
              const duration = sourceRange?.duration?.value || 0

              const clip: OTIOClip = {
                name: item.name || 'Unnamed Clip',
                track_name: trackInfo.name,
                timeline_position_frames: timelinePosition,
                source_in_frames: sourceRange?.start_time?.value || 0,
                source_out_frames: (sourceRange?.start_time?.value || 0) + duration,
                duration_frames: duration,
                nle_unique_id: item.metadata?.nle_unique_id
              }

              trackInfo.clips.push(clip)
              timelinePosition += duration
              maxDuration = Math.max(maxDuration, timelinePosition)
            } else if (item.OTIO_SCHEMA?.includes('Gap')) {
              // Handle gaps (empty space on timeline)
              const duration = item.source_range?.duration?.value || 0
              timelinePosition += duration
            } else if (item.OTIO_SCHEMA?.includes('Stack')) {
              // Handle nested sequences/stacks - advance position but don't expand
              const duration = item.source_range?.duration?.value || 0
              timelinePosition += duration
            }
          }
        }

        if (trackInfo.clips.length > 0) {
          tracks.push(trackInfo)
        }
      }
    }

    return { tracks, frameRate, duration: maxDuration }
  } catch (error) {
    console.error('[OTIO] Failed to parse OTIO timeline:', error)
    return { tracks: [], frameRate: 24, duration: 0 }
  }
}

/**
 * Get clip color based on diff data
 */
function getClipColor(clipName: string, nleId: string | undefined, diffData?: { changes: ClipDiffInfo[] }): string {
  if (!diffData?.changes) return 'unchanged'

  const change = diffData.changes.find(c =>
    c.clip_name === clipName || (nleId && c.nle_unique_id === nleId)
  )

  return change?.change_type || 'unchanged'
}

/**
 * Get VFX status color classes
 */
function getVfxStatusColorClasses(status: string): string {
  switch (status) {
    case 'yellow':
      return 'bg-amber-500 border-amber-400 text-white'
    case 'green':
      return 'bg-emerald-500 border-emerald-400 text-white'
    case 'in_progress':
      return 'bg-blue-500 border-blue-400 text-white'
    case 'review_ready':
      return 'bg-yellow-500 border-yellow-400 text-white'
    case 'approved':
      return 'bg-green-600 border-green-500 text-white'
    case 'omit':
      return 'bg-red-500 border-red-400 text-white opacity-60'
    case 'back_in':
      return 'bg-cyan-500 border-cyan-400 text-white'
    default:
      return ''
  }
}

/**
 * Get Tailwind classes for clip based on change type
 */
function getClipColorClasses(changeType: string): string {
  switch (changeType) {
    case 'added':
      return 'bg-green-600 border-green-500 text-white'
    case 'removed':
      return 'bg-red-600 border-red-500 text-white opacity-60'
    case 'trimmed':
    case 'moved':
    case 'metadata':
      return 'bg-blue-600 border-blue-500 text-white'
    case 'unchanged':
    default:
      return 'bg-gray-700 border-gray-600 text-white'
  }
}

/**
 * Convert frames to timecode string (HH:MM:SS:FF)
 */
function framesToTimecode(frames: number, frameRate: number): string {
  const fps = Math.round(frameRate)
  const totalSeconds = Math.floor(frames / fps)
  const remainingFrames = Math.floor(frames % fps)

  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}:${remainingFrames.toString().padStart(2, '0')}`
}

// =============================================
// MAIN COMPONENT
// =============================================

export function OTIOTimelineViewer({
  otioTimeline,
  compareOtioTimeline,
  diffData,
  vfxShots,
  className = ''
}: OTIOTimelineViewerProps) {
  const [mode, setMode] = useState<'view' | 'compare'>(compareOtioTimeline ? 'compare' : 'view')

  // Parse primary timeline
  const { tracks: rawTracks, frameRate, duration } = useMemo(
    () => parseOTIOTimeline(otioTimeline),
    [otioTimeline]
  )

  // Reverse tracks for proper video track ordering (V3, V2, V1 instead of V1, V2, V3)
  // Video tracks should display from bottom to top in NLE style
  const tracks = useMemo(() => {
    return [...rawTracks].reverse()
  }, [rawTracks])

  const PIXELS_PER_FRAME = 2 // Scale factor for timeline width
  const CLIP_HEIGHT = 60
  const TRACK_HEIGHT = 80
  const RULER_HEIGHT = 30

  if (tracks.length === 0) {
    return (
      <Card className={`p-8 ${className}`}>
        <div className="text-center text-muted-foreground">
          <p>No timeline data available</p>
        </div>
      </Card>
    )
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Mode Toggle */}
      {compareOtioTimeline && (
        <div className="flex items-center gap-2">
          <Button
            variant={mode === 'view' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode('view')}
          >
            <Eye className="h-4 w-4 mr-2" />
            View
          </Button>
          <Button
            variant={mode === 'compare' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode('compare')}
          >
            <GitCompare className="h-4 w-4 mr-2" />
            Compare
          </Button>
        </div>
      )}

      {/* Timeline Info */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span>Frame Rate: {frameRate.toFixed(3)} fps</span>
        <span>Duration: {framesToTimecode(duration, frameRate)}</span>
        <span>Tracks: {tracks.length}</span>
        {mode === 'compare' && diffData && (
          <div className="flex items-center gap-2 ml-auto">
            <Badge variant="outline" className="bg-green-600/10 text-green-500 border-green-600">
              +{diffData.summary.added}
            </Badge>
            <Badge variant="outline" className="bg-red-600/10 text-red-500 border-red-600">
              -{diffData.summary.removed}
            </Badge>
            <Badge variant="outline" className="bg-blue-600/10 text-blue-500 border-blue-600">
              ~{diffData.summary.trimmed + diffData.summary.moved}
            </Badge>
            {diffData.summary.vfx_frame_changes !== undefined && diffData.summary.vfx_frame_changes > 0 && (
              <Badge variant="outline" className="bg-purple-600/10 text-purple-400 border-purple-600">
                <Wand2 className="h-3 w-3 mr-1" />
                {diffData.summary.vfx_frame_changes}
              </Badge>
            )}
            {diffData.summary.retranscode_required !== undefined && diffData.summary.retranscode_required > 0 && (
              <Badge variant="outline" className="bg-orange-600/10 text-orange-400 border-orange-600">
                <AlertCircle className="h-3 w-3 mr-1" />
                {diffData.summary.retranscode_required} Retranscode{diffData.summary.retranscode_required !== 1 ? 's' : ''} Required
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Timeline Container */}
      <Card className="p-4 bg-secondary/30 overflow-x-auto">
        <div style={{ minWidth: duration * PIXELS_PER_FRAME }}>
          {/* Timeline Ruler */}
          <div
            className="relative border-b border-border mb-2"
            style={{ height: RULER_HEIGHT }}
          >
            {/* Frame markers every ~100 frames */}
            {Array.from({ length: Math.ceil(duration / 100) }).map((_, i) => {
              const frame = i * 100
              const position = frame * PIXELS_PER_FRAME

              return (
                <div
                  key={i}
                  className="absolute top-0 text-xs text-muted-foreground"
                  style={{ left: position }}
                >
                  <div className="h-2 w-px bg-border" />
                  <span className="ml-1">{framesToTimecode(frame, frameRate)}</span>
                </div>
              )
            })}
          </div>

          {/* Tracks */}
          <div className="space-y-2">
            {tracks.map((track, trackIndex) => (
              <div key={trackIndex} className="relative" style={{ height: TRACK_HEIGHT }}>
                {/* Track Label */}
                <div className="absolute left-0 top-0 bottom-0 w-24 flex items-center justify-center bg-secondary/50 border border-border rounded-l text-xs font-medium">
                  {track.name}
                </div>

                {/* Track Lane */}
                <div
                  className="absolute left-24 right-0 top-0 bottom-0 bg-secondary/20 border border-border rounded-r"
                  style={{ height: TRACK_HEIGHT }}
                >
                  {/* Clips */}
                  {track.clips.map((clip, clipIndex) => {
                    const changeType = mode === 'compare'
                      ? getClipColor(clip.name, clip.nle_unique_id, diffData)
                      : 'unchanged'

                    // Check if this clip matches a VFX shot
                    // VFX shots store SOURCE frame ranges, not timeline positions
                    // So we need to compare source_in/source_out to shot frame ranges
                    const matchingVfxShot = vfxShots?.find(shot => {
                      // Match by SOURCE frame range overlap (not timeline position)
                      const clipSourceStart = clip.source_in_frames
                      const clipSourceEnd = clip.source_out_frames
                      const shotStart = shot.frame_range_start
                      const shotEnd = shot.frame_range_end

                      // Check if source ranges overlap
                      return clipSourceStart <= shotEnd && clipSourceEnd >= shotStart
                    })

                    // Determine color: VFX status color takes precedence over change type
                    let colorClasses = getClipColorClasses(changeType)
                    if (matchingVfxShot && mode === 'view') {
                      const vfxColor = getVfxStatusColorClasses(matchingVfxShot.status)
                      if (vfxColor) {
                        colorClasses = vfxColor
                      }
                    }

                    const clipWidth = clip.duration_frames * PIXELS_PER_FRAME
                    const clipLeft = clip.timeline_position_frames * PIXELS_PER_FRAME

                    return (
                      <motion.div
                        key={clipIndex}
                        className={`absolute rounded border-2 p-2 overflow-hidden cursor-pointer ${colorClasses}`}
                        style={{
                          left: clipLeft,
                          width: clipWidth,
                          height: CLIP_HEIGHT,
                          top: (TRACK_HEIGHT - CLIP_HEIGHT) / 2
                        }}
                        title={`${clip.name}\n${clip.duration_frames} frames\nIn: ${clip.source_in_frames} Out: ${clip.source_out_frames}${matchingVfxShot ? `\nVFX: ${matchingVfxShot.shot_name} (${matchingVfxShot.status})` : ''}`}
                        whileHover={{ scale: 1.05, zIndex: 10 }}
                        whileTap={{ scale: 0.98 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                      >
                        <div className="text-xs font-medium truncate">{clip.name}</div>
                        <div className="text-xs opacity-80 truncate">
                          {clip.duration_frames}f
                          {matchingVfxShot && <span className="ml-1">• {matchingVfxShot.status}</span>}
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Legend */}
      {mode === 'compare' && (
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-green-600" />
            <span>Added</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-red-600 opacity-60" />
            <span>Removed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-blue-600" />
            <span>Modified</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-gray-700" />
            <span>Unchanged</span>
          </div>
        </div>
      )}

      {/* VFX Shot Changes */}
      {mode === 'compare' && diffData?.vfx_changes && diffData.vfx_changes.length > 0 && (
        <Card className="p-4 bg-purple-950/20 border-purple-800/30">
          <div className="flex items-center gap-2 mb-3">
            <Wand2 className="h-4 w-4 text-purple-400" />
            <h4 className="text-sm font-semibold text-purple-300">VFX Shot Changes</h4>
            <Badge variant="outline" className="ml-auto bg-purple-600/10 text-purple-400 border-purple-600">
              {diffData.vfx_changes.length} shot{diffData.vfx_changes.length !== 1 ? 's' : ''}
            </Badge>
          </div>
          <div className="space-y-2">
            {diffData.vfx_changes.map((change, index) => {
              const isExtended = change.frame_difference > 0
              const isShortened = change.frame_difference < 0

              return (
                <div
                  key={index}
                  className="p-3 rounded-md bg-secondary/50 border border-purple-800/20 hover:bg-secondary/70 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-sm truncate">{change.shot_name}</p>
                        {isExtended && (
                          <Badge variant="outline" className="text-xs bg-green-600/10 text-green-400 border-green-600">
                            <ArrowUpRight className="h-3 w-3 mr-1" />
                            +{change.frame_difference}f
                          </Badge>
                        )}
                        {isShortened && (
                          <Badge variant="outline" className="text-xs bg-orange-600/10 text-orange-400 border-orange-600">
                            <ArrowDownRight className="h-3 w-3 mr-1" />
                            {change.frame_difference}f
                          </Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <div>
                          <span className="opacity-60">Old:</span>{' '}
                          <span className="font-mono">{change.old_start}-{change.old_end}</span>
                          <span className="opacity-60 ml-1">({change.old_duration}f)</span>
                        </div>
                        <div>
                          <span className="opacity-60">New:</span>{' '}
                          <span className="font-mono">{change.new_start}-{change.new_end}</span>
                          <span className="opacity-60 ml-1">({change.new_duration}f)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </Card>
      )}
    </div>
  )
}
